import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Grid,
  TextField,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../Snackbar/SnackbarContext";
import DeleteConfirmDialog from "../DeleteConfirmDialog";
import DropDownField from "../../DropDownField";

const DiaryListing = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [diaries, setDiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [classes, setClasses] = useState([]);

  // Filter States
  const [filterBranch, setFilterBranch] = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [filterDate, setFilterDate] = useState("");

  // Delete Dialog State
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedDiaryGuid, setSelectedDiaryGuid] = useState("");

  const role = localStorage.getItem("role") || "staff";
  const userGuid = localStorage.getItem("guid") || "";

  useEffect(() => {
    fetchBranches();
    fetchClasses();
    fetchDiaries();
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(apiEndpoints.branches, {
        headers: getHeaders(),
      });
      if (res.data && res.data.data) {
        setBranches(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching branches:", err);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${apiEndpoints.class}?action=getAll`, {
        headers: getHeaders(),
      });
      if (res.data && res.data.status) {
        setClasses(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching classes:", err);
    }
  };

  const fetchDiaries = async (branch = "", clName = "", date = "") => {
    setLoading(true);
    try {
      let url = apiEndpoints.student_diary;
      const params = [];
      if (branch) params.push(`branch_guid=${branch}`);
      if (clName) params.push(`class_guid=${clName}`);
      if (date) params.push(`date=${date}`);
      
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }

      const res = await axios.get(url, { headers: getHeaders() });
      if (res.data && res.data.status) {
        setDiaries(res.data.data || []);
      } else {
        showSnackbar(res.data.message || "Failed to load diaries", "error");
      }
    } catch (err) {
      console.error("Error loading diaries:", err);
      showSnackbar("Failed to load diaries from server", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchDiaries(filterBranch, filterClass, filterDate);
  };

  const handleReset = () => {
    setFilterBranch("");
    setFilterClass("");
    setFilterDate("");
    fetchDiaries("", "", "");
  };

  const handleDeleteClick = (guid) => {
    setSelectedDiaryGuid(guid);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const res = await axios.delete(
        `${apiEndpoints.student_diary}?guid=${selectedDiaryGuid}`,
        { headers: getHeaders() }
      );
      if (res.data && res.data.status) {
        showSnackbar("Diary entry deleted successfully", "success");
        fetchDiaries(filterBranch, filterClass, filterDate);
      } else {
        showSnackbar(res.data.message || "Failed to delete entry", "error");
      }
    } catch (err) {
      console.error("Error deleting diary entry:", err);
      showSnackbar("Error deleting diary entry", "error");
    } finally {
      setDeleteOpen(false);
    }
  };

  const isEditable = (diary) => {
    if (role === "admin") return true;
    return diary.created_by === userGuid;
  };

  const prefixPath = role === "admin" ? "/admin" : "/teacher";

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5" fontWeight={600}>
            Student Diary
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate(`${prefixPath}/diary/add`)}
          >
            Add Diary Entry
          </Button>
        </Box>

        {/* Filter Panel */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <DropDownField
              label="Branch"
              value={filterBranch}
              onChange={(val) => setFilterBranch(val)}
              options={branches.map((b) => ({
                guid: b.guid,
                name: b.name,
              }))}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DropDownField
              label="Class"
              value={filterClass}
              onChange={(val) => setFilterClass(val)}
              options={classes.map((c) => ({
                guid: c.guid,
                name: c.name,
              }))}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3} display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              Filter
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleReset}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" my={5}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Branch</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Homework</TableCell>
                  <TableCell>Classwork</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {diaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No diary entries found.
                    </TableCell>
                  </TableRow>
                ) : (
                  diaries.map((diary) => (
                    <TableRow key={diary.guid}>
                      <TableCell>{diary.date}</TableCell>
                      <TableCell>{diary.branch_name}</TableCell>
                      <TableCell>{diary.class_name}</TableCell>
                      <TableCell>
                        {diary.target_type === "students" 
                          ? `Specific Students (${diary.student_guids ? diary.student_guids.split(",").length : 0})`
                          : "Whole Class"}
                      </TableCell>
                      <TableCell>
                        {diary.homework
                          ? diary.homework.substring(0, 40) +
                            (diary.homework.length > 40 ? "..." : "")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {diary.classwork
                          ? diary.classwork.substring(0, 40) +
                            (diary.classwork.length > 40 ? "..." : "")
                          : "-"}
                      </TableCell>
                      <TableCell>{diary.creator_name || "-"}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="info"
                          onClick={() =>
                            navigate(`${prefixPath}/diary/${diary.guid}`)
                          }
                        >
                          <ViewIcon />
                        </IconButton>
                        {isEditable(diary) && (
                          <>
                            <IconButton
                              color="primary"
                              onClick={() =>
                                navigate(
                                  `${prefixPath}/diary/edit/${diary.guid}`
                                )
                              }
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(diary.guid)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Diary Entry"
        content="Are you sure you want to delete this student diary entry? This action cannot be undone."
      />
    </Box>
  );
};

export default DiaryListing;
